/*
  femme cart drawer
  Version: V67
  Date: 2026-09-24
  File: assets/femme-cart-drawer.js
  V64 changes:
  - Retains V63's Vitall checkout fix, V62 fast EasyDraw rendering, IVFN gate
    and established cart-state repair.
  - Upgrades EasyDraw integrity from one device per Shopify order to one device
    per patient EasyDraw group.
  - Multiple patients can therefore legitimately have separate EasyDraw devices
    in the same basket, while multiple selected tests for the same patient still
    share one £49 device.
  - Duplicate/orphan repair now operates independently per EasyDraw Group Key.
  - Removing one EasyDraw device clears only the tests linked to that device;
    other patient groups remain untouched.
  - Linked cart styling now decorates every EasyDraw group rather than only the
    first device found in the cart.
  - Checkout validation requires exactly one device per selected EasyDraw group
    and independently rejects any group containing mismatched patient details.
  - Ordinary cart checkout, Community Picks, empty-cart recovery and all
    existing Vitall/IVFN protections remain unchanged.
  Baseline: V63 Vitall checkout-safe cart JavaScript.
*/
(() => {
  const FemmeCartCountStore = {
    key: 'femme-cart-confirmed-item-count',

    read() {
      try {
        const value = window.sessionStorage.getItem(this.key);
        if (value === null || value === '') return null;

        const count = Number(value);
        return Number.isFinite(count) && count >= 0 ? Math.floor(count) : null;
      } catch (error) {
        return null;
      }
    },

    write(count) {
      const normalised = Math.max(0, Math.floor(Number(count) || 0));

      try {
        window.sessionStorage.setItem(this.key, String(normalised));
      } catch (error) {
        // sessionStorage can be unavailable in privacy-restricted contexts.
      }

      return normalised;
    },
  };

  const FemmeCartNavigationSync = {
    inFlight: null,

    formatAccessibleCount(count) {
      return `${count} ${count === 1 ? 'item' : 'items'}`;
    },

    getCartLinkBaseLabel(link) {
      if (!(link instanceof HTMLElement)) return 'Cart';

      if (link.dataset.femmeCartBaseLabel) {
        return link.dataset.femmeCartBaseLabel;
      }

      const labelledText =
        link.querySelector('.mobile-sticky-bar__text')?.textContent?.trim() ||
        link.querySelector('.visually-hidden')?.textContent?.trim() ||
        'Cart';

      const baseLabel = labelledText.replace(/[,\s]+\d+\s+items?\.?$/i, '').trim() || 'Cart';
      link.dataset.femmeCartBaseLabel = baseLabel;
      return baseLabel;
    },

    updateAccessibleCountLabel(element, count) {
      if (!(element instanceof Element)) return;

      const currentLabel = element.getAttribute('aria-label') || '';
      const accessibleCount = this.formatAccessibleCount(count);
      let nextLabel = currentLabel;

      if (!nextLabel) {
        nextLabel = accessibleCount;
      } else if (/\d+\+?/.test(nextLabel)) {
        nextLabel = nextLabel.replace(/\d+\+?/, String(count));
      } else {
        nextLabel = accessibleCount;
      }

      if (/\bitems?\b/i.test(nextLabel)) {
        nextLabel = nextLabel.replace(/\bitems?\b/i, count === 1 ? 'item' : 'items');
      }

      element.setAttribute('aria-label', nextLabel);
    },

    updateCartCountElement(element, count) {
      if (!(element instanceof HTMLElement)) return;

      const visibleCount = count > 99 ? '99+' : String(count);
      const femmeValue = element.querySelector('.femme-cart-count__value');

      if (femmeValue instanceof HTMLElement) {
        femmeValue.textContent = visibleCount;
        femmeValue.classList.toggle('text-sm', count > 99);
      } else if (count > 99) {
        element.innerHTML = '<span class="text-sm">99+</span>';
      } else {
        element.textContent = visibleCount;
      }

      element.classList.toggle('cart-count--small-medium', count > 99);
      element.hidden = count === 0;
      this.updateAccessibleCountLabel(element, count);
    },

    updateFallbackBadge(element, count) {
      if (!(element instanceof HTMLElement)) return;
      if (element.matches('cart-count') || element.closest('cart-count')) return;

      const visibleCount = count > 99 ? '99+' : String(count);
      const valueSelector = '[data-cart-count-value], [data-cart-count], .cart-count__value, .cart-count-bubble__text';
      const valueTarget = element.matches(valueSelector) ? element : element.querySelector(valueSelector);

      if (valueTarget instanceof HTMLElement) {
        valueTarget.textContent = visibleCount;
      }

      const badgeContainer = element.matches('[data-cart-count-bubble], .cart-count-bubble')
        ? element
        : element.closest('[data-cart-count-bubble], .cart-count-bubble');

      if (badgeContainer instanceof HTMLElement) {
        badgeContainer.hidden = count === 0;
        this.updateAccessibleCountLabel(badgeContainer, count);
      } else if (element.matches(valueSelector)) {
        element.hidden = count === 0;
        this.updateAccessibleCountLabel(element, count);
      }
    },

    updateCartLink(link, count) {
      if (!(link instanceof HTMLElement)) return;

      const baseLabel = this.getCartLinkBaseLabel(link);
      link.setAttribute('aria-label', `${baseLabel}, ${this.formatAccessibleCount(count)}`);
      link.dataset.femmeCartItemCount = String(count);
    },

    applyCount(count, { persist = true } = {}) {
      const normalised = Math.max(0, Math.floor(Number(count) || 0));

      if (persist) {
        FemmeCartCountStore.write(normalised);
      }

      document.querySelectorAll('cart-count').forEach((element) => {
        this.updateCartCountElement(element, normalised);
      });

      document
        .querySelectorAll('[data-cart-count-bubble], .cart-count-bubble, [data-cart-count-value], [data-cart-count], .cart-count__value, .cart-count-bubble__text')
        .forEach((element) => this.updateFallbackBadge(element, normalised));

      document
        .querySelectorAll('.cart-drawer-button[aria-controls="CartDrawer"], a[aria-controls="CartDrawer"][href*="/cart"]')
        .forEach((link) => this.updateCartLink(link, normalised));

      document.documentElement.classList.toggle('cart-has-items', normalised > 0);
      document.documentElement.dataset.femmeCartItemCount = String(normalised);

      return normalised;
    },

    restoreCachedCount() {
      const cachedCount = FemmeCartCountStore.read();
      if (cachedCount === null) return;

      this.applyCount(cachedCount, { persist: false });
    },

    rememberCart(cart) {
      if (!cart || !Object.prototype.hasOwnProperty.call(cart, 'item_count')) return;
      this.applyCount(cart.item_count);
    },

    async reconcile(reason = 'manual') {
      if (this.inFlight) return this.inFlight;

      this.inFlight = (async () => {
        const cart = await FemmeCartStateSync.fetchCart();
        if (!FemmeCartStateSync.isCartLike(cart)) return null;

        FemmeCartStateSync.lastCart = cart;
        this.rememberCart(cart);

        // Reuse the established cart section repair. It remains background-only:
        // schedule() never opens the drawer and only renders fresh sections if
        // the currently cached drawer markup disagrees with the authoritative cart.
        FemmeCartStateSync.schedule(cart);

        return cart;
      })();

      try {
        return await this.inFlight;
      } finally {
        this.inFlight = null;
      }
    },

    init() {
      if (typeof window.femmeSynchroniseCart !== 'function') {
        window.femmeSynchroniseCart = (reason = 'manual') => this.reconcile(reason);
      }

      if (window.__femmeCartPageshowBound !== true) {
        window.__femmeCartPageshowBound = true;
        window.addEventListener('pageshow', () => {
          window.femmeSynchroniseCart('pageshow');
        });
      }

      window.femmeSynchroniseCart('initialisation');
    },
  };

  // This runs synchronously as soon as the deferred cart script executes. On a
  // reconstructed history page it replaces stale server-rendered Cart 0 markup
  // with the last confirmed count before the authoritative /cart.js request ends.
  FemmeCartNavigationSync.restoreCachedCount();

  class FemmeCartSubscription extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.input = this.querySelector('.femme-cart-subscription__input');
      this.status = this.querySelector('.femme-cart-subscription__status');

      if (!this.input || this.input.disabled) return;
      this.input.addEventListener('change', this.handleChange.bind(this));
    }

    async handleChange(event) {
      event?.stopPropagation();

      const checked = this.input.checked;
      const planId = this.dataset.sellingPlanId;
      const lineKey = this.dataset.lineKey;
      const lineIndex = Number(this.dataset.lineIndex || 0);

      if (!lineKey || (checked && !planId)) {
        this.input.checked = !checked;
        return;
      }

      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = '';

      try {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections },
          })
        );

        const body = JSON.stringify({
          id: lineKey,
          selling_plan: checked ? Number(planId) : null,
          sections,
        });

        const response = await fetch(FoxTheme.routes.cart_change_url, {
          ...FoxTheme.utils.fetchConfig(),
          body,
        });
        const cart = await response.json();

        if (!response.ok || cart.errors) {
          throw new Error(cart.description || cart.message || cart.errors || 'Unable to update subscription');
        }

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          sources: 'femme-cart-subscription',
          cart,
          target: this.input,
          line: lineIndex,
          name: 'selling_plan',
        });

        window.setTimeout(() => {
          if (this.isConnected) {
            this.classList.remove('is-loading');
            this.removeAttribute('aria-busy');
          }
        }, 600);
      } catch (error) {
        console.error(error);
        this.input.checked = !checked;
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
        if (this.status) this.status.textContent = 'Try again';
      }
    }
  }

  if (!customElements.get('femme-cart-subscription')) {
    customElements.define('femme-cart-subscription', FemmeCartSubscription);
  }

  class FemmeCartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('[data-femme-recommendations-ready]')) {
        this.classList.remove('hidden');
        return;
      }

      if (this.dataset.loading === 'true' || !this.dataset.url) return;
      this.dataset.loading = 'true';

      const load = () => this.loadRecommendations();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(load, { timeout: 1200 });
      } else {
        setTimeout(load, 80);
      }
    }

    async loadRecommendations() {
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) return;

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const replacement = documentFragment.querySelector('femme-cart-recommendations');

        if (!replacement || !replacement.querySelector('[data-femme-recommendations-ready]')) {
          this.classList.add('hidden');
          return;
        }

        this.innerHTML = replacement.innerHTML;
        this.classList.remove('hidden');
      } catch (error) {
        console.error(error);
      }
    }
  }

  if (!customElements.get('femme-cart-recommendations')) {
    customElements.define('femme-cart-recommendations', FemmeCartRecommendations);
  }

  const FemmeCartChatLayer = {
    hiddenTargets: new Map(),

    getDrawer() {
      return document.getElementById('CartDrawer');
    },

    isDrawerOpen() {
      const drawer = this.getDrawer();
      return Boolean(drawer && drawer.hasAttribute('open'));
    },

    collectTargets(root = document, results = new Set()) {
      const selectors = [
        '.activator-nudge',
        'shopify-chat',
        '#ShopifyChat',
        '[data-shopify-chat]',
        '[data-shopify-inbox]',
      ];

      selectors.forEach((selector) => {
        try {
          root.querySelectorAll(selector).forEach((node) => {
            const nodeRoot = node.getRootNode && node.getRootNode();
            if (nodeRoot instanceof ShadowRoot && nodeRoot.host) {
              results.add(nodeRoot.host);
            } else {
              results.add(node);
            }
          });
        } catch (error) {
          // Ignore selectors unsupported by a particular root.
        }
      });

      try {
        root.querySelectorAll('*').forEach((node) => {
          if (node.shadowRoot) {
            this.collectTargets(node.shadowRoot, results);
          }
        });
      } catch (error) {
        // Ignore inaccessible roots.
      }

      if (root === document) {
        document.querySelectorAll('iframe').forEach((frame) => {
          const haystack = `${frame.src || ''} ${frame.title || ''} ${frame.id || ''} ${frame.className || ''}`.toLowerCase();
          if (haystack.includes('chat') || haystack.includes('inbox')) {
            results.add(frame);
          }
        });
      }

      return results;
    },

    hide() {
      this.collectTargets().forEach((target) => {
        if (!(target instanceof HTMLElement) || this.hiddenTargets.has(target)) return;

        this.hiddenTargets.set(target, {
          visibility: target.style.getPropertyValue('visibility'),
          visibilityPriority: target.style.getPropertyPriority('visibility'),
          pointerEvents: target.style.getPropertyValue('pointer-events'),
          pointerEventsPriority: target.style.getPropertyPriority('pointer-events'),
        });

        target.style.setProperty('visibility', 'hidden', 'important');
        target.style.setProperty('pointer-events', 'none', 'important');
      });
    },

    restore() {
      this.hiddenTargets.forEach((previous, target) => {
        if (!target || !target.style) return;

        if (previous.visibility) {
          target.style.setProperty('visibility', previous.visibility, previous.visibilityPriority || '');
        } else {
          target.style.removeProperty('visibility');
        }

        if (previous.pointerEvents) {
          target.style.setProperty('pointer-events', previous.pointerEvents, previous.pointerEventsPriority || '');
        } else {
          target.style.removeProperty('pointer-events');
        }
      });

      this.hiddenTargets.clear();
    },

    sync() {
      if (this.isDrawerOpen()) {
        this.hide();

        window.clearTimeout(this.retryTimer);
        this.retryTimer = window.setTimeout(() => this.hide(), 350);
        window.clearTimeout(this.lateRetryTimer);
        this.lateRetryTimer = window.setTimeout(() => this.hide(), 1200);
      } else {
        this.restore();
      }
    },

    init() {
      const drawer = this.getDrawer();
      if (!drawer || drawer.dataset.femmeChatLayerBound === 'true') return;

      drawer.dataset.femmeChatLayerBound = 'true';
      drawer.addEventListener('toggle', () => this.sync());
      drawer.addEventListener('drawer:handleAfterShow', () => this.sync());
      drawer.addEventListener('drawer:handleAfterHide', () => this.sync());

      this.sync();
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => FemmeCartChatLayer.init(), { once: true });
  } else {
    FemmeCartChatLayer.init();
  }

  const FemmeCartQuickOptions = {
    open(trigger) {
      const dialogId = trigger.getAttribute('aria-controls');
      const dialog = dialogId ? document.getElementById(dialogId) : null;
      if (!dialog || typeof dialog.showModal !== 'function') return;

      window.clearTimeout(dialog._femmeCloseTimer);
      window.clearTimeout(dialog._femmeAddResetTimer);
      dialog.classList.remove('is-closing', 'is-adding', 'is-choosing');
      dialog.dataset.femmeClosing = 'false';
      dialog.dataset.femmeAutoAdding = 'false';

      this.resetChoices(dialog);
      this.syncSellingPlans(dialog);

      if (!dialog.open) {
        dialog.showModal();
      }
    },

    close(dialog) {
      if (!dialog || typeof dialog.close !== 'function' || !dialog.open) return;
      if (dialog.dataset.femmeClosing === 'true') return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion) {
        dialog.close();
        this.afterClose(dialog);
        return;
      }

      dialog.dataset.femmeClosing = 'true';
      dialog.classList.add('is-closing');

      window.clearTimeout(dialog._femmeCloseTimer);
      dialog._femmeCloseTimer = window.setTimeout(() => {
        dialog.close();
        this.afterClose(dialog);
      }, 180);
    },

    afterClose(dialog) {
      dialog.classList.remove('is-closing', 'is-adding', 'is-choosing');
      dialog.dataset.femmeClosing = 'false';
      dialog.dataset.femmeAutoAdding = 'false';
      this.resetChoices(dialog);
    },

    resetChoices(dialog) {
      const choices = dialog.querySelector('[data-femme-quick-options-choices]');
      const chooseButton = dialog.querySelector('[data-femme-quick-options-choose]');
      const variantSelect = dialog.querySelector('[data-femme-quick-options-variant]');
      const planSelect = dialog.querySelector('[data-femme-quick-options-plan]');

      if (choices) choices.hidden = true;
      if (chooseButton) chooseButton.hidden = false;

      if (variantSelect) {
        variantSelect.value = '';
      }

      if (planSelect) {
        planSelect.value = '';
      }

      dialog.classList.remove('is-choosing');
    },

    revealChoices(dialog) {
      const choices = dialog.querySelector('[data-femme-quick-options-choices]');
      const chooseButton = dialog.querySelector('[data-femme-quick-options-choose]');

      if (!choices) return;

      choices.hidden = false;
      if (chooseButton) chooseButton.hidden = true;
      dialog.classList.add('is-choosing');

      this.syncSellingPlans(dialog);

      const firstChoice = choices.querySelector('select:not([disabled])');
      if (!firstChoice) return;

      try {
        firstChoice.focus({ preventScroll: true });
      } catch (error) {
        firstChoice.focus();
      }

      if (typeof firstChoice.showPicker === 'function') {
        try {
          firstChoice.showPicker();
        } catch (error) {
          // Some browsers restrict programmatic picker opening. The focused
          // select remains visible as the safe fallback in those browsers.
        }
      }
    },

    syncSellingPlans(scope) {
      const variantSelect = scope.querySelector('[data-femme-quick-options-variant]');
      const planSelect = scope.querySelector('[data-femme-quick-options-plan]');
      if (!planSelect) return;

      const variantId = variantSelect
        ? String(variantSelect.value || '')
        : String(scope.querySelector('input[name="id"]')?.value || '');

      Array.from(planSelect.options).forEach((option) => {
        if (option.dataset.placeholder === 'true') {
          option.hidden = false;
          option.disabled = true;
          return;
        }

        const matches = String(option.dataset.variantId || '') === variantId;
        option.hidden = !matches;
        option.disabled = !matches;
      });

      const selected = planSelect.selectedOptions[0];
      if (!selected || selected.hidden || selected.disabled) {
        planSelect.value = '';
      }
    },

    choicesReady(dialog) {
      const variantSelect = dialog.querySelector('[data-femme-quick-options-variant]');
      const planSelect = dialog.querySelector('[data-femme-quick-options-plan]');

      if (variantSelect && !variantSelect.value) return false;
      if (planSelect && !planSelect.value) return false;

      return true;
    },

    submitSelection(dialog) {
      if (!dialog || dialog.dataset.femmeAutoAdding === 'true') return;
      if (!this.choicesReady(dialog)) return;

      const form = dialog.querySelector('[data-femme-quick-options-form]');
      const submitButton = form?.querySelector('.femme-cart-quick-options__auto-submit');

      if (!form || !submitButton || submitButton.disabled) return;

      dialog.dataset.femmeAutoAdding = 'true';
      dialog.classList.add('is-adding');

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitButton);
      } else {
        submitButton.click();
      }

      window.clearTimeout(dialog._femmeAddResetTimer);
      dialog._femmeAddResetTimer = window.setTimeout(() => {
        if (dialog.open && dialog.dataset.femmeAutoAdding === 'true') {
          dialog.dataset.femmeAutoAdding = 'false';
          dialog.classList.remove('is-adding');
        }
      }, 4000);
    },

    handleSelection(target, dialog) {
      if (target.matches('[data-femme-quick-options-variant]')) {
        this.syncSellingPlans(dialog);

        const planSelect = dialog.querySelector('[data-femme-quick-options-plan]');
        if (planSelect) {
          window.requestAnimationFrame(() => planSelect.focus());
          return;
        }
      }

      this.submitSelection(dialog);
    },

    handleCartUpdated() {
      document.querySelectorAll('.femme-cart-quick-options[open]').forEach((dialog) => {
        if (dialog.dataset.femmeAutoAdding !== 'true') return;
        this.close(dialog);
      });
    },

    init() {
      if (document.documentElement.dataset.femmeQuickOptionsBound === 'true') return;
      document.documentElement.dataset.femmeQuickOptionsBound = 'true';

      // V38: capture popup-open clicks before theme-level bubble handlers can
      // intercept them. This guarantees one click / tap opens the product card.
      document.addEventListener('click', (event) => {
        const openTrigger = event.target.closest?.('[data-femme-quick-options-open]');
        if (!openTrigger) return;

        event.preventDefault();
        event.stopPropagation();
        this.open(openTrigger);
      }, true);

      document.addEventListener('click', (event) => {
        const openTrigger = event.target.closest('[data-femme-quick-options-open]');
        if (openTrigger) {
          event.preventDefault();
          this.open(openTrigger);
          return;
        }

        const chooseTrigger = event.target.closest('[data-femme-quick-options-choose]');
        if (chooseTrigger) {
          event.preventDefault();
          this.revealChoices(chooseTrigger.closest('.femme-cart-quick-options'));
          return;
        }

        const closeTrigger = event.target.closest('[data-femme-quick-options-close]');
        if (closeTrigger) {
          event.preventDefault();
          this.close(closeTrigger.closest('dialog'));
          return;
        }

        if (event.target instanceof HTMLDialogElement && event.target.classList.contains('femme-cart-quick-options')) {
          const rect = event.target.getBoundingClientRect();
          const inside =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;

          if (!inside) {
            this.close(event.target);
          }
        }
      });

      document.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;
        if (!target.matches('[data-femme-quick-options-variant], [data-femme-quick-options-plan]')) return;

        const dialog = target.closest('.femme-cart-quick-options');
        if (dialog) this.handleSelection(target, dialog);
      });

      document.addEventListener('submit', (event) => {
        const form = event.target.closest?.('[data-femme-quick-options-form]');
        if (!form) return;

        const dialog = form.closest('.femme-cart-quick-options');
        if (!dialog) return;

        dialog.dataset.femmeAutoAdding = 'true';
        dialog.classList.add('is-adding');
      });

      document.addEventListener('cancel', (event) => {
        if (!(event.target instanceof HTMLDialogElement)) return;
        if (!event.target.classList.contains('femme-cart-quick-options')) return;

        event.preventDefault();
        this.close(event.target);
      });

      document.addEventListener('cart:updated', () => this.handleCartUpdated());
    },
  };

  FemmeCartQuickOptions.init();


  const FemmeCartOfferSearch = {
    recommendationCache: new Map(),
    relevanceRequests: new WeakMap(),

    normalise(value) {
      return String(value || '')
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    },

    getItems(scope) {
      if (!scope) return [];
      return Array.from(scope.querySelectorAll('[data-femme-offer-search-item]'));
    },

    getSortValue(scope) {
      return scope?.querySelector('[data-femme-offer-sort]')?.value || 'relevance';
    },

    getSortUi(scope) {
      return scope?.querySelector('[data-femme-offer-sort-ui]') || null;
    },

    getSortLabel(value = 'relevance') {
      switch (value) {
        case 'newest':
          return 'Newest > Oldest';
        case 'oldest':
          return 'Oldest > Newest';
        case 'price-asc':
          return 'Price Low to High';
        case 'price-desc':
          return 'Price High to Low';
        case 'alphabetical':
          return 'Alphabetically';
        case 'relevance':
        default:
          return 'Relevancy';
      }
    },

    syncSortUi(scope, value = 'relevance') {
      const sortUi = this.getSortUi(scope);
      const sortInput = scope?.querySelector('[data-femme-offer-sort]');
      const sortLabel = sortUi?.querySelector('[data-femme-offer-sort-label]');
      const sortOptions = sortUi ? Array.from(sortUi.querySelectorAll('[data-femme-offer-sort-option]')) : [];

      if (sortInput) {
        sortInput.value = value;
      }

      if (sortLabel) {
        sortLabel.textContent = this.getSortLabel(value);
      }

      sortOptions.forEach((option) => {
        const isActive = option.dataset.value === value;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    },

    openSortMenu(scope) {
      const sortUi = this.getSortUi(scope);
      if (!sortUi) return;

      const menu = sortUi.querySelector('[data-femme-offer-sort-menu]');
      const trigger = sortUi.querySelector('[data-femme-offer-sort-trigger]');
      if (!menu || !trigger) return;

      sortUi.classList.add('is-open');
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    },

    closeSortMenu(scope) {
      const sortUi = this.getSortUi(scope);
      if (!sortUi) return;

      const menu = sortUi.querySelector('[data-femme-offer-sort-menu]');
      const trigger = sortUi.querySelector('[data-femme-offer-sort-trigger]');
      if (!menu || !trigger) return;

      sortUi.classList.remove('is-open');
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    },

    toggleSortMenu(scope) {
      const sortUi = this.getSortUi(scope);
      if (!sortUi) return;
      if (sortUi.classList.contains('is-open')) {
        this.closeSortMenu(scope);
      } else {
        document.querySelectorAll('[data-femme-offer-sort-ui].is-open').forEach((openUi) => {
          this.closeSortMenu(openUi.closest('.femme-cart-offer-picker'));
        });
        this.openSortMenu(scope);
      }
    },

    getSearchValue(scope) {
      return scope?.querySelector('[data-femme-offer-search]')?.value || '';
    },

    getNumber(item, key) {
      const value = Number(item?.dataset?.[key]);
      return Number.isFinite(value) ? value : 0;
    },

    compareText(a, b) {
      return String(a || '').localeCompare(String(b || ''), undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    },

    originalOrder(a, b) {
      return this.getNumber(a, 'femmeOfferOriginalIndex') - this.getNumber(b, 'femmeOfferOriginalIndex');
    },

    compareItems(a, b, sortValue) {
      let difference = 0;

      switch (sortValue) {
        case 'newest':
          difference = this.getNumber(b, 'femmeOfferProductCreated') - this.getNumber(a, 'femmeOfferProductCreated');
          break;
        case 'oldest':
          difference = this.getNumber(a, 'femmeOfferProductCreated') - this.getNumber(b, 'femmeOfferProductCreated');
          break;
        case 'price-asc':
          difference = this.getNumber(a, 'femmeOfferProductPrice') - this.getNumber(b, 'femmeOfferProductPrice');
          break;
        case 'price-desc':
          difference = this.getNumber(b, 'femmeOfferProductPrice') - this.getNumber(a, 'femmeOfferProductPrice');
          break;
        case 'alphabetical':
          difference = this.compareText(a.dataset.femmeOfferProductTitle, b.dataset.femmeOfferProductTitle);
          break;
        case 'relevance':
        default:
          difference = this.getNumber(b, 'femmeOfferRelevanceScore') - this.getNumber(a, 'femmeOfferRelevanceScore');
          break;
      }

      return difference || this.originalOrder(a, b);
    },

    sort(scope, sortValue = this.getSortValue(scope)) {
      if (!scope) return;

      const list = scope.querySelector('[data-femme-offer-search-results]');
      if (!list) return;

      const items = this.getItems(scope);
      items.sort((a, b) => this.compareItems(a, b, sortValue));

      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(item));
      list.appendChild(fragment);
    },

    filter(scope, rawQuery = '') {
      if (!scope) return;

      const query = this.normalise(rawQuery);
      const items = this.getItems(scope);
      const emptyTarget = scope.querySelector('[data-femme-offer-search-empty]');
      let visibleCount = 0;

      items.forEach((item) => {
        const haystack = this.normalise(item.dataset.femmeOfferSearchText || '');
        const matches = !query || haystack.includes(query);
        item.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      if (emptyTarget) {
        emptyTarget.hidden = visibleCount !== 0;
      }
    },

    apply(scope) {
      if (!scope) return;
      this.sort(scope, this.getSortValue(scope));
      this.filter(scope, this.getSearchValue(scope));
    },

    getSeedProductIds(scope) {
      const raw = scope?.dataset?.femmeOfferRelevanceSeeds || '';
      return Array.from(
        new Set(
          raw
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean)
        )
      );
    },

    async fetchRelatedProducts(productId) {
      if (this.recommendationCache.has(productId)) {
        return this.recommendationCache.get(productId);
      }

      const request = (async () => {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=related`;
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`Recommendation request failed with ${response.status}`);
        }

        const payload = await response.json();
        return Array.isArray(payload?.products) ? payload.products : [];
      })().catch((error) => {
        console.warn('femme cart offer relevance unavailable', error);
        return [];
      });

      this.recommendationCache.set(productId, request);
      return request;
    },

    ensureRelevance(scope) {
      if (!scope) return Promise.resolve();
      if (scope.dataset.femmeOfferRelevanceLoaded === 'true') return Promise.resolve();

      const existingRequest = this.relevanceRequests.get(scope);
      if (existingRequest) return existingRequest;

      const seedProductIds = this.getSeedProductIds(scope);
      if (seedProductIds.length === 0) {
        scope.dataset.femmeOfferRelevanceLoaded = 'true';
        return Promise.resolve();
      }

      scope.dataset.femmeOfferRelevanceLoading = 'true';

      const request = (async () => {
        try {
          const recommendationSets = await Promise.all(
            seedProductIds.map((productId) => this.fetchRelatedProducts(productId))
          );

          const scores = new Map();

          recommendationSets.forEach((products) => {
            products.forEach((product, index) => {
              const id = String(product?.id || '');
              if (!id) return;

              const rankWeight = Math.max(1, 10 - index);
              scores.set(id, (scores.get(id) || 0) + rankWeight);
            });
          });

          this.getItems(scope).forEach((item) => {
            const productId = String(item.dataset.femmeOfferProductId || '');
            item.dataset.femmeOfferRelevanceScore = String(scores.get(productId) || 0);
          });

          scope.dataset.femmeOfferRelevanceLoaded = 'true';
        } finally {
          scope.dataset.femmeOfferRelevanceLoading = 'false';
          this.relevanceRequests.delete(scope);

          if (this.getSortValue(scope) === 'relevance') {
            this.apply(scope);
          }
        }
      })();

      this.relevanceRequests.set(scope, request);
      return request;
    },

    prewarm(root = document) {
      const pickers = Array.from(root.querySelectorAll?.('.femme-cart-offer-picker') || []);
      pickers.forEach((picker) => {
        this.ensureRelevance(picker);
      });
    },

    reset(scope) {
      if (!scope) return;

      const input = scope.querySelector('[data-femme-offer-search]');

      if (input) input.value = '';
      this.syncSortUi(scope, 'relevance');
      this.closeSortMenu(scope);

      this.apply(scope);
      this.ensureRelevance(scope);
    },

    init() {
      if (document.documentElement.dataset.femmeOfferSearchBound === 'true') return;
      document.documentElement.dataset.femmeOfferSearchBound = 'true';

      document.addEventListener('input', (event) => {
        const input = event.target.closest?.('[data-femme-offer-search]');
        if (!input) return;

        const picker = input.closest('.femme-cart-offer-picker');
        this.filter(picker, input.value);
      });

      document.addEventListener('click', (event) => {
        const sortTrigger = event.target.closest?.('[data-femme-offer-sort-trigger]');
        if (sortTrigger) {
          const picker = sortTrigger.closest('.femme-cart-offer-picker');
          this.toggleSortMenu(picker);
          return;
        }

        const sortOption = event.target.closest?.('[data-femme-offer-sort-option]');
        if (sortOption) {
          const picker = sortOption.closest('.femme-cart-offer-picker');
          const value = sortOption.dataset.value || 'relevance';
          this.syncSortUi(picker, value);
          this.closeSortMenu(picker);
          this.apply(picker);

          if (value === 'relevance') {
            this.ensureRelevance(picker);
          }
          return;
        }

        document.querySelectorAll('[data-femme-offer-sort-ui].is-open').forEach((openUi) => {
          if (openUi.contains(event.target)) return;
          this.closeSortMenu(openUi.closest('.femme-cart-offer-picker'));
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        document.querySelectorAll('[data-femme-offer-sort-ui].is-open').forEach((openUi) => {
          this.closeSortMenu(openUi.closest('.femme-cart-offer-picker'));
        });
      });

      document.addEventListener('cart:updated', () => {
        window.setTimeout(() => this.prewarm(), 0);
      });

      const prewarmOffers = () => this.prewarm();

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(prewarmOffers, { timeout: 500 });
      } else {
        window.setTimeout(prewarmOffers, 0);
      }
    },
  };

  FemmeCartOfferSearch.init();

  const FemmeCartOfferPicker = {
    async open(trigger) {
      const dialogId = trigger.getAttribute('aria-controls');
      const dialog = dialogId ? document.getElementById(dialogId) : null;
      if (!dialog || typeof dialog.showModal !== 'function') return;
      if (dialog.dataset.femmeOpening === 'true') return;

      dialog.dataset.femmeOpening = 'true';

      try {
        window.clearTimeout(dialog._femmeOfferCloseTimer);
        dialog.classList.remove('is-closing');
        dialog.dataset.femmeClosing = 'false';
        dialog._femmeReturnFocus = trigger;
        FemmeCartOfferSearch.reset(dialog);

        await FemmeCartOfferSearch.ensureRelevance(dialog);

        if (!dialog.isConnected) return;

        FemmeCartOfferSearch.apply(dialog);

        if (!dialog.open) {
          dialog.showModal();
        }
      } finally {
        dialog.dataset.femmeOpening = 'false';
      }
    },

    close(dialog, restoreFocus = true) {
      if (!dialog || typeof dialog.close !== 'function' || !dialog.open) return;
      if (dialog.dataset.femmeClosing === 'true') return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion) {
        dialog.close();
        this.afterClose(dialog, restoreFocus);
        return;
      }

      dialog.dataset.femmeClosing = 'true';
      dialog.classList.add('is-closing');

      window.clearTimeout(dialog._femmeOfferCloseTimer);
      dialog._femmeOfferCloseTimer = window.setTimeout(() => {
        dialog.close();
        this.afterClose(dialog, restoreFocus);
      }, 180);
    },

    afterClose(dialog, restoreFocus = true) {
      dialog.classList.remove('is-closing');
      dialog.dataset.femmeClosing = 'false';

      if (restoreFocus) {
        const returnFocus = dialog._femmeReturnFocus;
        if (returnFocus instanceof HTMLElement && returnFocus.isConnected) {
          window.requestAnimationFrame(() => returnFocus.focus());
        }
      }

      dialog._femmeReturnFocus = null;
    },

    closeOpenPickers() {
      document.querySelectorAll('.femme-cart-offer-picker[open]').forEach((dialog) => {
        this.close(dialog, false);
      });
    },

    init() {
      if (document.documentElement.dataset.femmeOfferPickerBound === 'true') return;
      document.documentElement.dataset.femmeOfferPickerBound = 'true';

      document.addEventListener('click', (event) => {
        const openTrigger = event.target.closest('[data-femme-offer-picker-open]');
        if (openTrigger) {
          event.preventDefault();
          this.open(openTrigger);
          return;
        }

        const closeTrigger = event.target.closest('[data-femme-offer-picker-close]');
        if (closeTrigger) {
          event.preventDefault();
          this.close(closeTrigger.closest('.femme-cart-offer-picker'));
          return;
        }

        if (
          event.target instanceof HTMLDialogElement &&
          event.target.classList.contains('femme-cart-offer-picker')
        ) {
          const rect = event.target.getBoundingClientRect();
          const inside =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;

          if (!inside) {
            this.close(event.target);
          }
        }
      });

      document.addEventListener('cancel', (event) => {
        if (!(event.target instanceof HTMLDialogElement)) return;
        if (!event.target.classList.contains('femme-cart-offer-picker')) return;

        event.preventDefault();
        this.close(event.target);
      });

      document.addEventListener('cart:updated', () => {
        this.closeOpenPickers();
      });
    },
  };

  FemmeCartOfferPicker.init();

  const FemmeCartOfferSlots = {
    remove(trigger) {
      const lineIndex = Number(trigger.dataset.lineIndex || 0);
      const nextQuantity = Math.max(0, Number(trigger.dataset.nextQuantity || 0));
      if (!lineIndex) return;

      const cartItems = document.querySelector('cart-items:not(.hidden)') || document.querySelector('cart-items');
      if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;

      cartItems.updateQuantity(lineIndex, nextQuantity);
    },

    init() {
      if (document.documentElement.dataset.femmeOfferSlotsBound === 'true') return;
      document.documentElement.dataset.femmeOfferSlotsBound = 'true';

      document.addEventListener('click', (event) => {
        const removeTrigger = event.target.closest('[data-femme-offer-remove]');
        if (!removeTrigger) return;

        event.preventDefault();
        event.stopPropagation();
        this.remove(removeTrigger);
      });
    },
  };

  FemmeCartOfferSlots.init();

  const FemmeCartStateSync = {
    timer: null,
    sequence: 0,
    lastCart: null,
    pendingAdd: false,
    guardTimer: null,
    slideTimer: null,
    observer: null,

    getDrawer() {
      return document.getElementById('CartDrawer');
    },

    getNodes(drawer = this.getDrawer()) {
      if (!drawer) return {};

      return {
        drawer,
        empty: drawer.querySelector('[id^="CartDrawerEmpty-"]'),
        body: drawer.querySelector('[id^="CartDrawerBody-"]'),
        footer: drawer.querySelector('[id^="CartDrawerFooter-"]'),
      };
    },

    isCartLike(value) {
      return Boolean(
        value &&
        Object.prototype.hasOwnProperty.call(value, 'item_count') &&
        Array.isArray(value.items)
      );
    },

    getCartFromPayload(payload) {
      if (!payload) return null;

      const candidates = [
        payload.cart,
        payload.cartData,
        payload.data?.cart,
        payload.data?.cartData,
        payload.detail?.cart,
        payload.detail?.cartData,
        payload.detail?.data?.cart,
        payload.detail?.data?.cartData,
      ];

      return candidates.find((candidate) => this.isCartLike(candidate)) || null;
    },

    async fetchCart() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        console.error('[femme cart] Unable to read current cart state.', error);
        return null;
      }
    },

    cartSignature(cart) {
      if (!this.isCartLike(cart)) return '';

      return cart.items
        .map((item) => `${String(item.variant_id || item.id || '')}:${Number(item.quantity || 0)}`)
        .sort()
        .join('|');
    },

    renderedSignature(body) {
      if (!body) return '';

      return Array.from(body.querySelectorAll('.cart-item'))
        .map((item) => `${String(item.dataset.variantId || '')}:${Number(item.dataset.quantity || 0)}`)
        .sort()
        .join('|');
    },

    bodyMatchesCart(body, cart) {
      if (!body || !this.isCartLike(cart)) return false;

      if (Number(cart.item_count || 0) === 0) {
        return body.querySelectorAll('.cart-item').length === 0;
      }

      const cartSignature = this.cartSignature(cart);
      const renderedSignature = this.renderedSignature(body);

      return Boolean(cartSignature) && cartSignature === renderedSignature;
    },

    uiMatchesCart(cart) {
      const { empty, body, footer } = this.getNodes();

      if (!empty || !body || !footer || !this.isCartLike(cart)) return false;

      const hasItems = Number(cart.item_count || 0) > 0;
      const emptyVisible = !empty.classList.contains('hidden');
      const bodyVisible = !body.classList.contains('hidden');
      const footerVisible = !footer.classList.contains('hidden');

      if (!hasItems) {
        return emptyVisible && !bodyVisible && !footerVisible;
      }

      return (
        !emptyVisible &&
        bodyVisible &&
        footerVisible &&
        this.bodyMatchesCart(body, cart)
      );
    },

    drawerIsActive(drawer = this.getDrawer()) {
      return Boolean(drawer?.hasAttribute('active'));
    },

    looksLikeAddControl(target) {
      if (!(target instanceof Element)) return false;

      const button = target.closest('button[name="add"], input[name="add"]');
      if (button) return true;

      const form = target.closest('form');
      if (!form) return false;

      const action = String(form.getAttribute('action') || '');

      return (
        action.includes('/cart/add') ||
        form.matches('[data-type="add-to-cart-form"]')
      );
    },

    looksLikeAddForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;

      const action = String(form.getAttribute('action') || '');

      return (
        action.includes('/cart/add') ||
        form.matches('[data-type="add-to-cart-form"]') ||
        Boolean(form.querySelector('[name="add"]'))
      );
    },

    armPendingAdd() {
      const drawer = this.getDrawer();
      if (!drawer || this.drawerIsActive(drawer)) return false;

      this.pendingAdd = true;
      drawer.classList.add('femme-cart-awaiting-refresh');
      drawer.setAttribute('aria-busy', 'true');

      window.clearTimeout(this.guardTimer);
      this.guardTimer = window.setTimeout(async () => {
        if (!this.pendingAdd) return;

        const cart = await this.fetchCart();
        if (this.isCartLike(cart)) {
          this.lastCart = cart;

          if (this.uiMatchesCart(cart)) {
            this.releaseGuard();
            return;
          }

          this.schedule(cart);
        }

        // Final safety release. This should almost never be reached, but it
        // prevents a third-party add-to-cart failure from leaving the drawer
        // permanently guarded.
        window.setTimeout(() => {
          if (this.pendingAdd) this.releaseGuard();
        }, 1800);
      }, 2600);

      return true;
    },

    releaseGuard() {
      const drawer = this.getDrawer();

      this.pendingAdd = false;
      window.clearTimeout(this.guardTimer);
      this.guardTimer = null;

      if (!drawer) return;

      drawer.removeAttribute('aria-busy');

      // If the theme has already marked the drawer active, the guard has kept
      // the stale empty-cart drawer completely off-screen. Swap to our release
      // class only after the populated DOM is verified, which gives us one
      // clean right-to-left slide with the correct cart already inside it.
      if (this.drawerIsActive(drawer)) {
        const inner = drawer.querySelector('.drawer__inner');

        if (inner) {
          // Force the guarded translateX(100%) state to be committed before
          // changing classes, otherwise the browser may collapse both states
          // into one frame and skip the slide.
          inner.getBoundingClientRect();
        }

        drawer.classList.remove('femme-cart-awaiting-refresh');
        drawer.classList.add('femme-cart-release-slide');

        window.clearTimeout(this.slideTimer);
        this.slideTimer = window.setTimeout(() => {
          drawer.classList.remove('femme-cart-release-slide');
        }, 480);

        return;
      }

      // If the native theme has not opened the drawer yet, simply remove the
      // guard. Its own subsequent open() call will use the original animation.
      drawer.classList.remove('femme-cart-awaiting-refresh');
    },

    releaseIfReady(cart = this.lastCart) {
      if (!this.pendingAdd || !this.isCartLike(cart)) return false;
      if (!this.uiMatchesCart(cart)) return false;

      this.releaseGuard();
      return true;
    },

    getSectionIds(drawer = this.getDrawer()) {
      if (!drawer) return [];

      let ids = [];

      if (typeof drawer.getSectionsToRender === 'function') {
        try {
          ids = drawer
            .getSectionsToRender()
            .map((section) => section?.id)
            .filter(Boolean);
        } catch (error) {
          console.error('[femme cart] Unable to read native cart sections.', error);
        }
      }

      if (!ids.length) {
        const content = drawer.querySelector('.drawer__content[id^="CartDrawer-"]');

        if (content?.id) {
          const fallbackId = content.id.replace(/^CartDrawer-/, '');
          if (fallbackId) ids.push(fallbackId);
        }
      }

      return [...new Set(ids)];
    },

    async fetchRenderedSections(sectionIds) {
      if (!sectionIds.length) return null;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(root, window.location.origin);

        url.searchParams.set('sections', sectionIds.join(','));
        url.searchParams.set('sections_url', window.location.pathname);
        url.searchParams.set('_femme_cart_refresh', String(Date.now()));

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const sections = await response.json();
        return sections && typeof sections === 'object' ? sections : null;
      } catch (error) {
        console.error('[femme cart] Unable to fetch fresh cart sections.', error);
        return null;
      }
    },

    renderedSectionsMatchCart(sections, cart) {
      if (!sections || !this.isCartLike(cart)) return false;

      for (const html of Object.values(sections)) {
        if (!html || typeof html !== 'string') continue;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const body = parsed.querySelector('[id^="CartDrawerBody-"]');

        if (body && this.bodyMatchesCart(body, cart)) {
          return true;
        }
      }

      return false;
    },

    renderWithTheme(drawer, sections, cart) {
      if (!drawer || !sections) return false;

      const payload = {
        ...cart,
        sections,
      };

      if (typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(payload);
          return true;
        } catch (error) {
          console.error('[femme cart] Native cart drawer render failed.', error);
        }
      }

      for (const html of Object.values(sections)) {
        if (!html || typeof html !== 'string') continue;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = parsed.querySelector('.drawer__content[id^="CartDrawer-"]');
        const current = drawer.querySelector('.drawer__content[id^="CartDrawer-"]');

        if (replacement && current) {
          current.innerHTML = replacement.innerHTML;
          return true;
        }
      }

      return false;
    },

    async wait(ms) {
      await new Promise((resolve) => window.setTimeout(resolve, ms));
    },

    async repairIfNeeded(cart, sequence) {
      if (!this.isCartLike(cart) || sequence !== this.sequence) return;

      // Let Sleek's own product-form/cart-drawer renderer finish first.
      await this.wait(260);

      if (sequence !== this.sequence) return;

      if (this.uiMatchesCart(cart)) {
        this.releaseIfReady(cart);
        return;
      }

      const drawer = this.getDrawer();
      if (!drawer) {
        this.releaseGuard();
        return;
      }

      const sectionIds = this.getSectionIds(drawer);
      if (!sectionIds.length) {
        this.releaseGuard();
        return;
      }

      const retryDelays = [0, 120, 250, 500, 850];

      for (const delay of retryDelays) {
        if (sequence !== this.sequence) return;
        if (delay) await this.wait(delay);

        if (sequence !== this.sequence) return;

        if (this.uiMatchesCart(cart)) {
          this.releaseIfReady(cart);
          return;
        }

        const latestCart = await this.fetchCart();
        if (this.isCartLike(latestCart)) {
          cart = latestCart;
          this.lastCart = latestCart;
        }

        const sections = await this.fetchRenderedSections(sectionIds);
        if (!sections) continue;

        // Never inject stale empty-cart HTML over a live populated cart.
        if (!this.renderedSectionsMatchCart(sections, cart)) {
          continue;
        }

        const currentDrawer = this.getDrawer();

        if (!this.renderWithTheme(currentDrawer, sections, cart)) {
          continue;
        }

        await new Promise((resolve) => window.requestAnimationFrame(resolve));

        if (this.uiMatchesCart(cart)) {
          this.releaseIfReady(cart);
          return;
        }
      }

      console.error('[femme cart] Cart drawer did not reach the current cart state after retries.');
      this.releaseGuard();
    },

    schedule(cart) {
      const resolvedCart = this.isCartLike(cart) ? cart : null;

      if (resolvedCart) {
        this.lastCart = resolvedCart;
        FemmeCartNavigationSync.rememberCart(resolvedCart);
      }

      this.sequence += 1;
      const sequence = this.sequence;

      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(async () => {
        let currentCart = resolvedCart;

        if (!this.isCartLike(currentCart)) {
          currentCart = await this.fetchCart();
        }

        if (!this.isCartLike(currentCart) || sequence !== this.sequence) return;

        this.lastCart = currentCart;
        FemmeCartNavigationSync.rememberCart(currentCart);

        if (this.uiMatchesCart(currentCart)) {
          this.releaseIfReady(currentCart);
          return;
        }

        this.repairIfNeeded(currentCart, sequence);
      }, 0);
    },

    initObserver() {
      const drawer = this.getDrawer();
      if (!drawer || this.observer) return;

      this.observer = new MutationObserver(() => {
        if (!this.pendingAdd || !this.isCartLike(this.lastCart)) return;
        this.releaseIfReady(this.lastCart);
      });

      this.observer.observe(drawer, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'active', 'data-quantity', 'data-variant-id'],
      });
    },

    init() {
      if (document.documentElement.dataset.femmeCartStateSyncBound === 'true') return;
      document.documentElement.dataset.femmeCartStateSyncBound = 'true';

      // Arm as early as pointerdown for mouse/touch users so the stale empty
      // drawer can never become visible between the click and the form submit.
      document.addEventListener('pointerdown', (event) => {
        if (!this.looksLikeAddControl(event.target)) return;
        this.armPendingAdd();
      }, true);

      // Keyboard submissions and programmatic form submissions are covered here.
      document.addEventListener('submit', (event) => {
        if (!this.looksLikeAddForm(event.target)) return;
        this.armPendingAdd();
      }, true);

      const handleEvent = (event) => {
        this.schedule(this.getCartFromPayload(event));
      };

      document.addEventListener('cart:updated', handleEvent);
      document.addEventListener('cart:refresh', handleEvent);
      window.addEventListener('cart:updated', handleEvent);
      window.addEventListener('cart:refresh', handleEvent);

      const pubsub = window.FoxTheme?.pubsub;
      const cartUpdateEvent = pubsub?.PUB_SUB_EVENTS?.cartUpdate;

      if (pubsub && typeof pubsub.subscribe === 'function' && cartUpdateEvent) {
        try {
          pubsub.subscribe(cartUpdateEvent, (payload) => {
            this.schedule(this.getCartFromPayload(payload));
          });
        } catch (error) {
          console.error('[femme cart] Unable to subscribe to theme cart updates.', error);
        }
      }

      this.initObserver();

      window.setTimeout(() => {
        this.initObserver();
        this.schedule(null);
      }, 500);
    },
  };

  FemmeCartStateSync.init();


  const FemmeEasyDrawCartIntegrity = {
    addonHandle: 'vitall-easydraw-self-collection-kit',
    eligibleProperty: '_Vitall EasyDraw Eligible',
    selectedProperty: '_Vitall EasyDraw Selected',
    groupProperty: '_Vitall EasyDraw Group Key',
    legacyBundleProperty: '_Vitall EasyDraw Bundle Key',
    addonProperty: '_Vitall EasyDraw Add-on',
    timer: null,
    mutating: false,
    rerunRequested: false,
    refreshPromise: null,
    lastHadEasyDrawRelationship: false,

    normalise(value) {
      return String(value ?? '').trim();
    },

    isTrue(value) {
      if (value === true) return true;
      return ['true', '1', 'yes'].includes(this.normalise(value).toLowerCase());
    },

    properties(item) {
      return item && item.properties && typeof item.properties === 'object'
        ? item.properties
        : {};
    },

    isAddon(item) {
      const properties = this.properties(item);
      const handle = this.normalise(item?.handle || item?.product_handle).toLowerCase();

      return (
        this.isTrue(properties[this.addonProperty]) ||
        handle === this.addonHandle
      );
    },

    groupKey(item) {
      const properties = this.properties(item);
      return this.normalise(
        properties[this.groupProperty] || properties[this.legacyBundleProperty]
      );
    },

    isEligibleTest(item) {
      if (!item || this.isAddon(item)) return false;
      return this.isTrue(this.properties(item)[this.eligibleProperty]);
    },

    isSelectedTest(item) {
      if (!item || this.isAddon(item)) return false;
      return this.isTrue(this.properties(item)[this.selectedProperty]);
    },

    cartHasEasyDrawRelationship(cart) {
      if (!FemmeCartStateSync.isCartLike(cart)) return false;

      return cart.items.some((item) => this.isAddon(item));
    },

    shouldForceDrawerRefresh(reason, cart) {
      const reasonText = this.normalise(reason).toLowerCase();
      const currentHasRelationship = this.cartHasEasyDrawRelationship(cart);

      const shouldRefresh =
        currentHasRelationship ||
        this.lastHadEasyDrawRelationship ||
        reasonText.includes('easydraw');

      this.lastHadEasyDrawRelationship = currentHasRelationship;
      return shouldRefresh;
    },

    getAddons(cart) {
      if (!FemmeCartStateSync.isCartLike(cart)) return [];
      return cart.items.filter((item) => this.isAddon(item));
    },

    getSelectedTestsForGroup(cart, groupKey) {
      if (!FemmeCartStateSync.isCartLike(cart)) return [];

      const targetGroupKey = this.normalise(groupKey);

      return cart.items.filter((item) => {
        if (!this.isSelectedTest(item)) return false;

        const itemGroupKey = this.groupKey(item);

        if (!targetGroupKey) {
          return !itemGroupKey;
        }

        return itemGroupKey === targetGroupKey;
      });
    },

    getAddonsForGroup(cart, groupKey) {
      if (!FemmeCartStateSync.isCartLike(cart)) return [];

      const targetGroupKey = this.normalise(groupKey);

      return this.getAddons(cart).filter((item) => {
        const itemGroupKey = this.groupKey(item);

        if (!targetGroupKey) {
          return !itemGroupKey;
        }

        return itemGroupKey === targetGroupKey;
      });
    },

    getSelectedTests(cart, addon = null) {
      if (!FemmeCartStateSync.isCartLike(cart)) return [];

      if (!addon) {
        return cart.items.filter((item) => this.isSelectedTest(item));
      }

      return this.getSelectedTestsForGroup(
        cart,
        this.groupKey(addon)
      );
    },

    buildRepairUpdates(cart) {
      const updates = {};
      const addons = this.getAddons(cart);

      if (!addons.length) {
        return updates;
      }

      const groups = new Map();

      addons.forEach((addon) => {
        const groupKey = this.groupKey(addon);
        const mapKey = groupKey || '__femme_legacy_easydraw__';

        if (!groups.has(mapKey)) {
          groups.set(mapKey, {
            groupKey,
            addons: []
          });
        }

        groups.get(mapKey).addons.push(addon);
      });

      groups.forEach((group) => {
        const groupAddons = group.addons;
        const selectedTests = this.getSelectedTestsForGroup(
          cart,
          group.groupKey
        );

        /*
          EasyDraw is paid once PER PATIENT GROUP. Each device survives only
          while at least one test is explicitly assigned to its own group.
        */
        if (!selectedTests.length) {
          groupAddons.forEach((item) => {
            if (item?.key) updates[item.key] = 0;
          });
          return;
        }

        const [keeper, ...duplicates] = groupAddons;

        if (keeper?.key && Number(keeper.quantity || 0) !== 1) {
          updates[keeper.key] = 1;
        }

        duplicates.forEach((item) => {
          if (item?.key) updates[item.key] = 0;
        });
      });

      return updates;
    },

    hasUpdates(updates) {
      return Boolean(updates && Object.keys(updates).length);
    },

    getUpdateUrl() {
      const explicit = window.FoxTheme?.routes?.cart_update_url;
      if (explicit) return explicit;

      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart/update.js`;
    },

    getFetchConfig() {
      if (typeof window.FoxTheme?.utils?.fetchConfig === 'function') {
        return window.FoxTheme.utils.fetchConfig();
      }

      return {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
    },

    getChangeUrl() {
      const explicit = window.FoxTheme?.routes?.cart_change_url;
      if (explicit) return explicit;

      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart/change.js`;
    },

    cleanSelectedProperties(item) {
      const properties = { ...this.properties(item) };
      delete properties[this.selectedProperty];
      delete properties[this.groupProperty];
      delete properties[this.legacyBundleProperty];
      return properties;
    },

    async clearSelectedLine(item) {
      if (!item?.key) return null;

      const response = await fetch(this.getChangeUrl(), {
        ...this.getFetchConfig(),
        credentials: 'same-origin',
        body: JSON.stringify({
          id: item.key,
          quantity: Number(item.quantity || 1),
          properties: this.cleanSelectedProperties(item),
        }),
      });

      const cart = await response.json();

      if (!response.ok || cart?.errors) {
        throw new Error(
          cart?.description ||
          cart?.message ||
          cart?.errors ||
          `Unable to return a Vitall test to standard collection (${response.status})`
        );
      }

      return cart;
    },

    async removeAddonAndClearSelections(trigger) {
      if (this.mutating) return false;
      this.mutating = true;

      const itemElement = trigger?.closest?.('.cart-item');
      itemElement?.setAttribute('aria-busy', 'true');
      trigger?.classList?.add('is-loading');

      try {
        let cart = await FemmeCartStateSync.fetchCart();
        if (!FemmeCartStateSync.isCartLike(cart)) {
          throw new Error('Unable to read the current cart before removing EasyDraw.');
        }

        const clickedLineKey = this.normalise(
          itemElement?.dataset?.cartLineKey
        );
        const clickedGroupKey = this.normalise(
          itemElement?.dataset?.femmeEasydrawGroupKey
        );

        const addons = this.getAddons(cart);
        const activeAddon =
          addons.find((item) => {
            return clickedLineKey && this.normalise(item?.key) === clickedLineKey;
          }) ||
          addons.find((item) => {
            return this.groupKey(item) === clickedGroupKey;
          }) ||
          null;

        if (!activeAddon) {
          throw new Error(
            'Unable to identify the EasyDraw device being removed.'
          );
        }

        const activeGroupKey = this.groupKey(activeAddon);
        const selectedTests = this.getSelectedTestsForGroup(
          cart,
          activeGroupKey
        );

        /*
          Clear only the selected tests belonging to THIS EasyDraw device.
          Other patient groups remain selected and their devices remain in cart.
        */
        for (const selectedTest of selectedTests) {
          const updatedCart = await this.clearSelectedLine(selectedTest);
          if (FemmeCartStateSync.isCartLike(updatedCart)) {
            cart = updatedCart;
          }
        }

        const updates = {};
        this.getAddonsForGroup(cart, activeGroupKey).forEach((item) => {
          if (item?.key) updates[item.key] = 0;
        });

        if (this.hasUpdates(updates)) {
          await this.updateCart(updates, 'femme-easydraw-remove-device');
        } else {
          await this.refreshDrawerFromPage(cart, 'femme-easydraw-remove-device');
          this.dispatchCartUpdated(cart, 'femme-easydraw-remove-device');
        }

        return true;
      } catch (error) {
        console.error('[femme EasyDraw] Unable to remove EasyDraw cleanly.', error);
        itemElement?.removeAttribute('aria-busy');
        trigger?.classList?.remove('is-loading');
        this.clearDrawerBusyState();
        return false;
      } finally {
        this.mutating = false;
      }
    },

    handleAddonRemoveClick(event) {
      const trigger = event.target.closest?.(
        '.femme-cart-drawer .cart-item__remove[is="cart-remove-item"], .femme-cart-drawer a.cart-item__remove'
      );

      if (!trigger) return;

      const item = trigger.closest('.cart-item');
      if (!item || item.dataset.femmeEasydrawAddon !== 'true') return;
      if (this.mutating) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.removeAddonAndClearSelections(trigger);
    },

    clearDrawerBusyState() {
      const drawer = FemmeCartStateSync.getDrawer();
      if (!drawer) return;

      drawer.removeAttribute('aria-busy');
      drawer.classList.remove(
        'femme-cart-awaiting-refresh',
        'femme-cart-release-slide'
      );

      drawer
        .querySelectorAll('.drawer__close-btn.is-loading, .cart-item__remove.is-loading')
        .forEach((element) => {
          element.classList.remove('is-loading');
          element.removeAttribute('aria-busy');
        });
    },

    clearDecorations(items) {
      items.forEach((item) => {
        item.classList.remove(
          'femme-cart-easydraw-pair--parent',
          'femme-cart-easydraw-pair--addon',
          'femme-cart-easydraw-group--test',
          'femme-cart-easydraw-group--first',
          'femme-cart-easydraw-group--last-test',
          'femme-cart-easydraw-group--addon'
        );
      });
    },

    decoratePairs(root = document) {
      const drawer =
        root?.matches?.('#CartDrawer')
          ? root
          : root?.querySelector?.('#CartDrawer') ||
            document.getElementById('CartDrawer');

      const list = drawer?.querySelector('.femme-cart-items');
      if (!list) return;

      const items = Array.from(
        list.querySelectorAll(':scope > .cart-item')
      );
      this.clearDecorations(items);

      const addons = items.filter(
        (item) => item.dataset.femmeEasydrawAddon === 'true'
      );

      if (!addons.length) return;

      addons.forEach((addon) => {
        const activeGroupKey = this.normalise(
          addon.dataset.femmeEasydrawGroupKey
        );

        const selectedTests = items.filter((item) => {
          if (item.dataset.femmeEasydrawParent !== 'true') {
            return false;
          }

          const testGroupKey = this.normalise(
            item.dataset.femmeEasydrawGroupKey
          );

          if (!activeGroupKey) {
            return !testGroupKey;
          }

          return testGroupKey === activeGroupKey;
        });

        if (!selectedTests.length) return;

        let cursor = selectedTests[0];

        /*
          Keep each patient/device group contiguous while preserving the
          selected tests' relative order inside that group.
        */
        selectedTests.slice(1).forEach((test) => {
          if (cursor.nextElementSibling !== test) {
            cursor.insertAdjacentElement('afterend', test);
          }
          cursor = test;
        });

        if (cursor.nextElementSibling !== addon) {
          cursor.insertAdjacentElement('afterend', addon);
        }

        if (selectedTests.length === 1) {
          selectedTests[0].classList.add(
            'femme-cart-easydraw-pair--parent'
          );
          addon.classList.add(
            'femme-cart-easydraw-pair--addon'
          );
          return;
        }

        selectedTests.forEach((test, index) => {
          test.classList.add(
            'femme-cart-easydraw-group--test'
          );

          if (index === 0) {
            test.classList.add(
              'femme-cart-easydraw-group--first'
            );
          }

          if (index === selectedTests.length - 1) {
            test.classList.add(
              'femme-cart-easydraw-group--last-test'
            );
          }
        });

        addon.classList.add(
          'femme-cart-easydraw-group--addon'
        );
      });
    },

    buildPageRefreshUrl() {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('_femme_cart_drawer_refresh', String(Date.now()));
      return url.toString();
    },

    async fetchFreshDrawerContent(cart) {
      const retryDelays = [0, 120, 300, 650];

      for (const delay of retryDelays) {
        if (delay) {
          await new Promise((resolve) => window.setTimeout(resolve, delay));
        }

        try {
          const response = await fetch(this.buildPageRefreshUrl(), {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
              Accept: 'text/html',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) continue;

          const htmlText = await response.text();
          const parsed = new DOMParser().parseFromString(htmlText, 'text/html');
          const replacement = parsed.querySelector(
            '#CartDrawer .drawer__content[id^="CartDrawer-"]'
          );

          if (!replacement) continue;

          const replacementBody = replacement.querySelector(
            '[id^="CartDrawerBody-"]'
          );

          if (
            replacementBody &&
            FemmeCartStateSync.isCartLike(cart) &&
            FemmeCartStateSync.bodyMatchesCart(replacementBody, cart)
          ) {
            return replacement;
          }
        } catch (error) {
          console.error('[femme EasyDraw] Full drawer refresh request failed.', error);
        }
      }

      return null;
    },

    async refreshDrawerFromSections(sections, cart, reason = 'easydraw-fast') {
      const drawer = FemmeCartStateSync.getDrawer();

      if (
        !drawer ||
        !sections ||
        typeof sections !== 'object' ||
        !FemmeCartStateSync.isCartLike(cart)
      ) {
        return false;
      }

      let replacement = null;

      for (const html of Object.values(sections)) {
        if (!html || typeof html !== 'string') continue;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const candidate = parsed.querySelector(
          '#CartDrawer .drawer__content[id^="CartDrawer-"], .drawer__content[id^="CartDrawer-"]'
        );

        if (!candidate) continue;

        const candidateBody = candidate.querySelector('[id^="CartDrawerBody-"]');

        if (
          candidateBody &&
          FemmeCartStateSync.bodyMatchesCart(candidateBody, cart)
        ) {
          replacement = candidate;
          break;
        }
      }

      const current = drawer.querySelector('.drawer__content[id^="CartDrawer-"]');

      if (!replacement || !current) {
        return false;
      }

      current.innerHTML = replacement.innerHTML;

      this.clearDrawerBusyState();

      FemmeCartStateSync.lastCart = cart;
      FemmeCartNavigationSync.rememberCart(cart);

      this.decoratePairs(drawer);

      try {
        FemmeCartOfferSearch.prewarm(drawer);
      } catch (error) {
        console.error('[femme EasyDraw] Unable to prewarm fast-rendered offer content.', error);
      }

      try {
        FemmeCartChatLayer.sync();
      } catch (error) {
        console.error('[femme EasyDraw] Unable to resynchronise chat layer after fast render.', error);
      }

      return true;
    },

    async refreshDrawerFromPage(cart, reason = 'easydraw') {
      if (this.refreshPromise) return this.refreshPromise;

      this.refreshPromise = (async () => {
        const drawer = FemmeCartStateSync.getDrawer();
        if (!drawer) return false;

        const resolvedCart = FemmeCartStateSync.isCartLike(cart)
          ? cart
          : await FemmeCartStateSync.fetchCart();

        if (!FemmeCartStateSync.isCartLike(resolvedCart)) return false;

        const replacement = await this.fetchFreshDrawerContent(resolvedCart);
        const current = drawer.querySelector(
          '.drawer__content[id^="CartDrawer-"]'
        );

        if (!replacement || !current) {
          console.warn(
            '[femme EasyDraw] Authoritative full-page cart drawer refresh was unavailable.',
            reason
          );
          return false;
        }

        /*
          Only replace CartDrawer's content. Do not replace the cart-drawer
          custom element, overlay, drawer inner shell, top close button or the
          V69 IVFN confirmation dialog.
        */
        current.innerHTML = replacement.innerHTML;

        this.clearDrawerBusyState();

        FemmeCartStateSync.lastCart = resolvedCart;
        FemmeCartNavigationSync.rememberCart(resolvedCart);

        this.decoratePairs(drawer);

        try {
          FemmeCartOfferSearch.prewarm(drawer);
        } catch (error) {
          console.error('[femme EasyDraw] Unable to prewarm refreshed offer content.', error);
        }

        try {
          FemmeCartChatLayer.sync();
        } catch (error) {
          console.error('[femme EasyDraw] Unable to resynchronise cart chat layer.', error);
        }

        return true;
      })();

      try {
        return await this.refreshPromise;
      } finally {
        this.refreshPromise = null;
      }
    },

    dispatchCartUpdated(cart, source) {
      if (!FemmeCartStateSync.isCartLike(cart)) return;

      FemmeCartStateSync.lastCart = cart;
      FemmeCartNavigationSync.rememberCart(cart);

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            source,
            cart,
          },
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            source,
            cart,
          },
        })
      );
    },

    async refreshAfterMutation(
      cart,
      source = 'femme-easydraw-external',
      sections = null
    ) {
      const resolvedCart = FemmeCartStateSync.isCartLike(cart)
        ? cart
        : await FemmeCartStateSync.fetchCart();

      if (!FemmeCartStateSync.isCartLike(resolvedCart)) return false;

      let refreshed = await this.refreshDrawerFromSections(
        sections,
        resolvedCart,
        `${source}:sections`
      );

      if (!refreshed) {
        refreshed = await this.refreshDrawerFromPage(resolvedCart, source);
      }

      if (refreshed) {
        this.lastHadEasyDrawRelationship =
          this.cartHasEasyDrawRelationship(resolvedCart);
        this.dispatchCartUpdated(resolvedCart, source);
      }

      return refreshed;
    },

    async updateCart(
      updates,
      source = 'femme-easydraw-integrity',
      options = {}
    ) {
      if (!this.hasUpdates(updates)) return null;

      const response = await fetch(this.getUpdateUrl(), {
        ...this.getFetchConfig(),
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });

      const cart = await response.json();

      if (!response.ok || cart?.errors) {
        throw new Error(
          cart?.description ||
          cart?.message ||
          cart?.errors ||
          `Unable to update EasyDraw order upgrade (${response.status})`
        );
      }

      FemmeCartStateSync.lastCart = cart;
      FemmeCartNavigationSync.rememberCart(cart);
      this.lastHadEasyDrawRelationship =
        this.cartHasEasyDrawRelationship(cart);

      if (options.refreshDrawer !== false) {
        await this.refreshDrawerFromPage(cart, source);
      }

      if (options.dispatch !== false) {
        this.dispatchCartUpdated(cart, source);
      }

      return cart;
    },

    schedule(reason = 'cart-update', delay = 80) {
      window.clearTimeout(this.timer);

      this.timer = window.setTimeout(() => {
        this.enforce(reason);
      }, delay);
    },

    async enforce(reason = 'manual', options = {}) {
      if (this.mutating) {
        this.rerunRequested = true;
        return;
      }

      this.mutating = true;

      const skipRefresh = options.skipRefresh === true;

      try {
        let cart = await FemmeCartStateSync.fetchCart();
        if (!FemmeCartStateSync.isCartLike(cart)) return;

        FemmeCartStateSync.lastCart = cart;
        FemmeCartNavigationSync.rememberCart(cart);

        const shouldRefreshDrawer =
          this.shouldForceDrawerRefresh(reason, cart);
        const updates = this.buildRepairUpdates(cart);

        if (this.hasUpdates(updates)) {
          cart = await this.updateCart(
            updates,
            `femme-easydraw-repair:${reason}`,
            skipRefresh
              ? {
                  refreshDrawer: false,
                  dispatch: false,
                }
              : {}
          );

          return cart;
        }

        if (shouldRefreshDrawer && !skipRefresh) {
          await this.refreshDrawerFromPage(cart, reason);
        } else {
          this.decoratePairs();
        }

        return cart;
      } catch (error) {
        console.error('[femme EasyDraw] Cart integrity repair failed.', error);
        return null;
      } finally {
        this.mutating = false;

        if (this.rerunRequested) {
          this.rerunRequested = false;
          this.schedule('queued-recheck', 120);
        }
      }
    },

    handleDrawerCloseClick(event) {
      const trigger = event.target.closest?.(
        '#CartDrawer > .drawer__inner > .drawer__close-btn[aria-controls="CartDrawer"]'
      );

      if (!trigger) return;

      const drawer = FemmeCartStateSync.getDrawer();
      if (!drawer) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.clearDrawerBusyState();
      trigger.classList.remove('is-loading');
      trigger.removeAttribute('aria-busy');

      try {
        if (typeof drawer.hide === 'function') {
          drawer.hide();
          return;
        }

        if (typeof drawer.close === 'function') {
          drawer.close();
          return;
        }
      } catch (error) {
        console.error('[femme EasyDraw] Native drawer close failed.', error);
      }

      drawer.removeAttribute('active');
      drawer.removeAttribute('open');
      drawer.setAttribute('hidden', '');

      try {
        FemmeCartChatLayer.restore();
      } catch (error) {
        console.error('[femme EasyDraw] Unable to restore chat layer.', error);
      }
    },

    init() {
      if (
        document.documentElement.dataset.femmeEasyDrawCartIntegrityBound ===
        'true'
      ) {
        return;
      }

      document.documentElement.dataset.femmeEasyDrawCartIntegrityBound = 'true';

      document.addEventListener(
        'click',
        (event) => this.handleAddonRemoveClick(event),
        true
      );

      document.addEventListener(
        'click',
        (event) => this.handleDrawerCloseClick(event),
        true
      );

      const scheduleFromEvent = (event) => {
        const source =
          event?.detail?.source ||
          event?.detail?.sources ||
          'cart-event';

        if (String(source).startsWith('femme-easydraw-')) {
          this.schedule('self-update-recheck', 180);
          return;
        }

        this.schedule(source, 140);
      };

      document.addEventListener('cart:updated', scheduleFromEvent);
      document.addEventListener('cart:refresh', scheduleFromEvent);
      window.addEventListener('cart:updated', scheduleFromEvent);
      window.addEventListener('cart:refresh', scheduleFromEvent);

      const pubsub = window.FoxTheme?.pubsub;
      const cartUpdateEvent = pubsub?.PUB_SUB_EVENTS?.cartUpdate;

      if (
        pubsub &&
        typeof pubsub.subscribe === 'function' &&
        cartUpdateEvent
      ) {
        try {
          pubsub.subscribe(cartUpdateEvent, (payload) => {
            const source =
              payload?.sources ||
              payload?.source ||
              payload?.data?.source ||
              'pubsub';

            this.schedule(source, 180);
          });
        } catch (error) {
          console.error(
            '[femme EasyDraw] Unable to subscribe to cart updates.',
            error
          );
        }
      }

      window.addEventListener('pageshow', () => {
        this.schedule('pageshow', 160);
      });

      window.femmeEasyDrawCartBridge = {
        refreshAfterMutation: (cart, source, sections = null) =>
          this.refreshAfterMutation(cart, source, sections),
        enforce: (reason = 'manual', options = {}) =>
          this.enforce(reason, options),
        decoratePairs: () => this.decoratePairs(),
      };

      this.decoratePairs();
      this.schedule('initialisation', 500);
    },
  };

  FemmeEasyDrawCartIntegrity.init();


  const FemmeIvfnCheckoutConfirmation = {
    productHandle: 'ivfn-12-month-premium-membership',
    bypassAttribute: 'data-femme-ivfn-checkout-bypass',
    pendingForm: null,
    pendingButton: null,
    busy: false,

    normalise(value) {
      return String(value ?? '').trim().toLowerCase();
    },

    getDialog() {
      return document.querySelector('[data-femme-cart-ivfn-confirmation]');
    },

    getError() {
      return this.getDialog()?.querySelector('[data-femme-cart-ivfn-error]') || null;
    },

    getKeepButton() {
      return this.getDialog()?.querySelector('[data-femme-cart-ivfn-keep]') || null;
    },

    getRemoveButton() {
      return this.getDialog()?.querySelector('[data-femme-cart-ivfn-remove]') || null;
    },

    getCloseButton() {
      return this.getDialog()?.querySelector('[data-femme-cart-ivfn-close]') || null;
    },

    getCheckoutForm() {
      return document.querySelector('[data-femme-cart-checkout-form]');
    },

    getCheckoutButton(form = this.getCheckoutForm()) {
      return form?.querySelector?.('[data-femme-cart-checkout-button]') || null;
    },

    getAcknowledgement() {
      return document.querySelector('[data-femme-cart-ivfn-acknowledge]');
    },

    acknowledgementChecked() {
      const acknowledgement = this.getAcknowledgement();
      return acknowledgement instanceof HTMLInputElement && acknowledgement.checked;
    },

    syncCheckoutAvailability() {
      const form = this.getCheckoutForm();
      const button = this.getCheckoutButton(form);
      if (!(button instanceof HTMLButtonElement)) return;

      const requiresAcknowledgement = this.renderedCartContainsIvfn(form);
      const allowed = !requiresAcknowledgement || this.acknowledgementChecked();

      button.disabled = !allowed;
      button.setAttribute('aria-disabled', allowed ? 'false' : 'true');
      button.classList.toggle('is-disabled', !allowed);
    },

    renderedCartContainsIvfn(form) {
      const drawer = form?.closest?.('#CartDrawer') || document.getElementById('CartDrawer');
      if (!drawer) return false;

      return Boolean(
        drawer.querySelector(
          `.cart-item[data-handle="${this.productHandle}"]`
        )
      );
    },

    isIvfnItem(item) {
      const handle = this.normalise(item?.handle || item?.product_handle);
      if (handle === this.productHandle) return true;

      const url = this.normalise(item?.url);
      return url.includes(`/products/${this.productHandle}`);
    },

    clearError() {
      const error = this.getError();
      if (!error) return;

      error.hidden = true;
      error.textContent = '';
    },

    setError(message) {
      const error = this.getError();
      if (!error) return;

      error.textContent = message;
      error.hidden = false;
    },

    setBusy(busy) {
      this.busy = Boolean(busy);
      const dialog = this.getDialog();
      const keepButton = this.getKeepButton();
      const removeButton = this.getRemoveButton();
      const closeButton = this.getCloseButton();

      dialog?.classList.toggle('is-working', this.busy);
      dialog?.setAttribute('aria-busy', this.busy ? 'true' : 'false');

      [keepButton, removeButton, closeButton].forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        button.disabled = this.busy;
      });
    },

    open(form, button) {
      const dialog = this.getDialog();
      if (!(dialog instanceof HTMLDialogElement)) return false;
      if (this.busy) return false;

      this.pendingForm = form;
      this.pendingButton = button || this.getCheckoutButton(form);
      this.clearError();
      dialog._femmeReturnFocus = this.pendingButton;

      if (!dialog.open) {
        dialog.showModal();
      }

      window.requestAnimationFrame(() => {
        const closeButton = this.getCloseButton();
        if (!(closeButton instanceof HTMLElement)) return;

        try {
          closeButton.focus({ preventScroll: true });
        } catch (error) {
          closeButton.focus();
        }
      });

      return true;
    },

    close({ restoreFocus = true, clearPending = true } = {}) {
      const dialog = this.getDialog();
      if (!(dialog instanceof HTMLDialogElement)) return;
      if (this.busy) return;

      const returnFocus = dialog._femmeReturnFocus;

      if (dialog.open) {
        dialog.close();
      }

      dialog._femmeReturnFocus = null;
      this.clearError();

      if (clearPending) {
        this.pendingForm = null;
        this.pendingButton = null;
      }

      if (restoreFocus && returnFocus instanceof HTMLElement && returnFocus.isConnected) {
        window.requestAnimationFrame(() => {
          try {
            returnFocus.focus({ preventScroll: true });
          } catch (error) {
            returnFocus.focus();
          }
        });
      }
    },

    getUpdateUrl() {
      const explicit = window.FoxTheme?.routes?.cart_update_url;
      if (explicit) return explicit;

      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart/update.js`;
    },

    getFetchConfig() {
      if (typeof window.FoxTheme?.utils?.fetchConfig === 'function') {
        return window.FoxTheme.utils.fetchConfig();
      }

      return {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
    },

    async fetchCart() {
      const cart = await FemmeCartStateSync.fetchCart();

      if (!FemmeCartStateSync.isCartLike(cart)) {
        throw new Error('Unable to verify the current cart.');
      }

      return cart;
    },

    buildRemovalUpdates(cart) {
      const updates = {};

      cart.items.forEach((item) => {
        if (!this.isIvfnItem(item) || !item?.key) return;
        updates[item.key] = 0;
      });

      return updates;
    },

    async removeIvfnFromCart() {
      const currentCart = await this.fetchCart();
      const updates = this.buildRemovalUpdates(currentCart);

      if (!Object.keys(updates).length) {
        return currentCart;
      }

      const response = await fetch(this.getUpdateUrl(), {
        ...this.getFetchConfig(),
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });

      const cart = await response.json();

      if (!response.ok || cart?.errors) {
        throw new Error(
          cart?.description ||
          cart?.message ||
          cart?.errors ||
          'Unable to remove IVFN Membership from your cart.'
        );
      }

      if (!FemmeCartStateSync.isCartLike(cart)) {
        throw new Error('Shopify did not return the updated cart.');
      }

      if (cart.items.some((item) => this.isIvfnItem(item))) {
        throw new Error('IVFN Membership could not be removed from your cart.');
      }

      FemmeCartStateSync.lastCart = cart;
      FemmeCartNavigationSync.rememberCart(cart);

      return cart;
    },

    async refreshAfterRemoval(cart) {
      let refreshed = false;

      try {
        if (
          typeof FemmeEasyDrawCartIntegrity?.refreshDrawerFromPage === 'function'
        ) {
          refreshed = await FemmeEasyDrawCartIntegrity.refreshDrawerFromPage(
            cart,
            'femme-ivfn-checkout-remove'
          );
        }
      } catch (error) {
        console.error('[femme IVFN] Drawer refresh after IVFN removal failed.', error);
      }

      if (!refreshed) {
        FemmeCartStateSync.schedule(cart);

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              source: 'femme-ivfn-checkout-remove',
              cart,
            },
          })
        );
      }

      return refreshed;
    },

    continueCheckout(form, button) {
      const currentForm =
        form instanceof HTMLFormElement && form.isConnected
          ? form
          : this.getCheckoutForm();

      if (!(currentForm instanceof HTMLFormElement)) {
        throw new Error('The checkout form is no longer available.');
      }

      const currentButton =
        button instanceof HTMLElement && button.isConnected
          ? button
          : this.getCheckoutButton(currentForm);

      currentForm.setAttribute(this.bypassAttribute, 'true');

      window.requestAnimationFrame(() => {
        try {
          if (typeof currentForm.requestSubmit === 'function') {
            currentForm.requestSubmit(currentButton || undefined);
          } else if (currentButton instanceof HTMLElement) {
            currentButton.click();
          } else {
            currentForm.submit();
          }
        } finally {
          window.setTimeout(() => {
            currentForm.removeAttribute(this.bypassAttribute);
          }, 0);
        }
      });
    },

    keepIvfn() {
      if (this.busy) return;
      this.close({ restoreFocus: true, clearPending: true });
    },

    async removeIvfnAndCheckout() {
      if (this.busy) return;

      const originalForm = this.pendingForm;
      const originalButton = this.pendingButton;

      this.clearError();
      this.setBusy(true);

      try {
        const cart = await this.removeIvfnFromCart();
        await this.refreshAfterRemoval(cart);

        this.setBusy(false);
        this.close({ restoreFocus: false, clearPending: true });

        this.syncCheckoutAvailability();
      } catch (error) {
        console.error('[femme IVFN] Unable to remove IVFN before checkout.', error);
        this.setBusy(false);
        this.setError(
          error?.message ||
          'We could not remove IVFN Membership from your cart. Please try again.'
        );
      }
    },

    handleSubmit(event) {
      const form = event.target;

      if (
        !(form instanceof HTMLFormElement) ||
        !form.matches('[data-femme-cart-checkout-form]')
      ) {
        return;
      }

      if (form.getAttribute(this.bypassAttribute) === 'true') {
        form.removeAttribute(this.bypassAttribute);
        return;
      }

      if (!this.renderedCartContainsIvfn(form)) {
        return;
      }

      if (!this.acknowledgementChecked()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.syncCheckoutAvailability();
        this.getAcknowledgement()?.focus();
        return;
      }

      return;
    },

    init() {
      if (
        document.documentElement.dataset.femmeIvfnCheckoutConfirmationBound ===
        'true'
      ) {
        return;
      }

      document.documentElement.dataset.femmeIvfnCheckoutConfirmationBound = 'true';

      document.addEventListener(
        'submit',
        (event) => this.handleSubmit(event),
        true
      );

      document.addEventListener('change', (event) => {
        if (event.target?.matches?.('[data-femme-cart-ivfn-acknowledge]')) {
          this.syncCheckoutAvailability();
        }
      });

      document.addEventListener('cart:updated', () => {
        window.requestAnimationFrame(() => this.syncCheckoutAvailability());
      });

      window.requestAnimationFrame(() => this.syncCheckoutAvailability());

      document.addEventListener('click', (event) => {
        const learnMoreButton = event.target.closest?.('[data-femme-cart-ivfn-learn-more]');
        if (learnMoreButton) {
          event.preventDefault();
          this.open(this.getCheckoutForm(), learnMoreButton);
          return;
        }

        const keepButton = event.target.closest?.('[data-femme-cart-ivfn-keep]');
        if (keepButton) {
          event.preventDefault();
          this.keepIvfn();
          return;
        }

        const removeButton = event.target.closest?.('[data-femme-cart-ivfn-remove]');
        if (removeButton) {
          event.preventDefault();
          this.removeIvfnAndCheckout();
          return;
        }

        const closeButton = event.target.closest?.('[data-femme-cart-ivfn-close]');
        if (closeButton) {
          event.preventDefault();
          this.close();
          return;
        }

        const dialog = this.getDialog();
        if (!(dialog instanceof HTMLDialogElement) || event.target !== dialog) return;

        const rect = dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        if (!inside) {
          this.close();
        }
      });

      document.addEventListener('cancel', (event) => {
        if (event.target !== this.getDialog()) return;
        event.preventDefault();
        this.close();
      });

      document.addEventListener('cart:updated', () => {
        if (!this.getDialog()?.open) return;

        const form = this.getCheckoutForm();
        if (!form || !this.renderedCartContainsIvfn(form)) {
          this.setBusy(false);
          this.close({ restoreFocus: false, clearPending: true });
        }
      });
    },
  };

  FemmeIvfnCheckoutConfirmation.init();


  const FemmeVitallCheckoutIntegrity = {
    requiredPatientProperties: [
      '_Vitall Patient First Name',
      '_Vitall Patient Last Name',
      '_Vitall Patient DOB',
      '_Vitall Patient Sex',
      '_Vitall Patient Email',
      '_Vitall Patient Phone',
      '_Vitall Patient Confirmed',
      '_Vitall Product Handle',
    ],

    bypassAttribute: 'data-femme-vitall-checkout-bypass',

    normalise(value) {
      return String(value ?? '').trim();
    },

    isTrue(value) {
      if (value === true) return true;
      return ['true', '1', 'yes'].includes(this.normalise(value).toLowerCase());
    },

    properties(item) {
      return item && item.properties && typeof item.properties === 'object'
        ? item.properties
        : {};
    },

    isVitallTest(item) {
      return this.isTrue(this.properties(item)['_Vitall Integration']);
    },

    isEasyDraw(item) {
      const properties = this.properties(item);
      const handle = this.normalise(item?.handle || item?.product_handle).toLowerCase();

      return (
        this.isTrue(properties['_Vitall EasyDraw Add-on']) ||
        handle === FemmeEasyDrawCartIntegrity.addonHandle
      );
    },

    cartContainsVitall(cart) {
      if (!FemmeCartStateSync.isCartLike(cart)) return false;

      return cart.items.some((item) => {
        return this.isVitallTest(item) || this.isEasyDraw(item);
      });
    },

    renderedCartContainsVitall(form) {
      const drawer = form?.closest?.('#CartDrawer') || document.getElementById('CartDrawer');
      if (!drawer) return false;

      return Boolean(
        drawer.querySelector(
          '.cart-item[data-femme-vitall-integration="true"], .cart-item[data-femme-easydraw-addon="true"]'
        )
      );
    },

    patientFingerprint(item) {
      const properties = this.properties(item);
      const phone = this.normalise(
        properties['_Vitall Patient Phone']
      ).replace(/\D/g, '');

      const parts = [
        this.normalise(
          properties['_Vitall Patient Email']
        ).toLowerCase(),
        this.normalise(
          properties['_Vitall Patient First Name']
        ).toLowerCase(),
        this.normalise(
          properties['_Vitall Patient Last Name']
        ).toLowerCase(),
        this.normalise(
          properties['_Vitall Patient DOB']
        ),
        this.normalise(
          properties['_Vitall Patient Sex']
        ).toLowerCase(),
        phone,
      ];

      return parts.every(Boolean) ? parts.join('|') : '';
    },

    easyDrawGroupKey(item) {
      const properties = this.properties(item);

      return this.normalise(
        properties['_Vitall EasyDraw Group Key'] ||
        properties['_Vitall EasyDraw Bundle Key']
      );
    },

    validatePatientProperties(item) {
      const properties = this.properties(item);
      const missing = this.requiredPatientProperties.filter((key) => {
        const value = properties[key];

        if (key === '_Vitall Patient Confirmed') {
          return !this.isTrue(value);
        }

        return this.normalise(value) === '';
      });

      return missing;
    },

    validate(cart) {
      const issues = [];
      if (!FemmeCartStateSync.isCartLike(cart)) {
        return ['We could not verify your cart. Please try again.'];
      }

      const selectedTests = [];
      const addons = [];

      cart.items.forEach((item) => {
        const properties = this.properties(item);

        if (this.isVitallTest(item)) {
          const title = this.normalise(item.product_title || item.title) || 'Vitall test';

          if (Number(item.quantity || 0) !== 1) {
            issues.push(
              `${title} must be purchased as one individual patient test.`
            );
          }

          const missingPatientProperties = this.validatePatientProperties(item);

          if (missingPatientProperties.length) {
            issues.push(
              `${title} is missing patient information and needs to be removed and added again.`
            );
          }

          if (this.isTrue(properties['_Vitall EasyDraw Selected'])) {
            selectedTests.push(item);
          }
        }

        if (this.isEasyDraw(item)) {
          addons.push(item);

          if (Number(item.quantity || 0) !== 1) {
            issues.push('EasyDraw must remain as one order-level collection upgrade.');
          }
        }
      });

      const selectedGroups = new Map();
      const addonGroups = new Map();

      selectedTests.forEach((item) => {
        const groupKey = this.easyDrawGroupKey(item);
        const mapKey = groupKey || '__femme_legacy_easydraw__';

        if (!selectedGroups.has(mapKey)) {
          selectedGroups.set(mapKey, {
            groupKey,
            items: []
          });
        }

        selectedGroups.get(mapKey).items.push(item);
      });

      addons.forEach((item) => {
        const groupKey = this.easyDrawGroupKey(item);
        const mapKey = groupKey || '__femme_legacy_easydraw__';

        if (!addonGroups.has(mapKey)) {
          addonGroups.set(mapKey, {
            groupKey,
            items: []
          });
        }

        addonGroups.get(mapKey).items.push(item);
      });

      selectedGroups.forEach((group, mapKey) => {
        const groupAddons = addonGroups.get(mapKey)?.items || [];

        if (groupAddons.length !== 1) {
          issues.push(
            'Each EasyDraw patient group must contain exactly one EasyDraw device.'
          );
        }

        const fingerprints = Array.from(
          new Set(
            group.items
              .map((item) => this.patientFingerprint(item))
              .filter(Boolean)
          )
        );

        if (fingerprints.length > 1) {
          issues.push(
            'EasyDraw can only be shared by tests for the same patient. Please remove and re-add the affected test with the correct patient/device selection.'
          );
        }
      });

      addonGroups.forEach((group, mapKey) => {
        const linkedTests = selectedGroups.get(mapKey)?.items || [];

        if (group.items.length > 1) {
          issues.push(
            'The same EasyDraw patient group contains more than one device.'
          );
        }

        if (linkedTests.length === 0) {
          issues.push(
            'An EasyDraw device no longer has a selected Vitall test in the cart.'
          );
        }
      });

      return [...new Set(issues)];
    },

    getCheckoutForm() {
      return document.querySelector('[data-femme-cart-checkout-form]');
    },

    getStatus(form) {
      return form?.querySelector?.('[data-femme-cart-checkout-status]') || null;
    },

    getButton(form) {
      return form?.querySelector?.('[data-femme-cart-checkout-button]') || null;
    },

    clearStatus(form) {
      const status = this.getStatus(form);
      if (!status) return;

      status.hidden = true;
      status.textContent = '';
      status.classList.remove('is-error');
    },

    setError(form, issues) {
      const status = this.getStatus(form);
      if (!status) return;

      status.textContent =
        issues.length > 1
          ? 'One or more health tests in your cart need attention before checkout. Please remove and re-add the affected test.'
          : issues[0];

      status.classList.add('is-error');
      status.hidden = false;
    },

    setChecking(form, checking) {
      const button = this.getButton(form);
      if (!button) return;

      button.classList.toggle('is-checking', checking);

      if (checking) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    },

    continueCheckout(form, button) {
      const currentForm =
        form instanceof HTMLFormElement && form.isConnected
          ? form
          : this.getCheckoutForm();

      if (!(currentForm instanceof HTMLFormElement)) {
        throw new Error(
          'The live checkout form could not be resolved after validation.'
        );
      }

      const currentButton =
        button instanceof HTMLElement && button.isConnected
          ? button
          : this.getButton(currentForm);

      currentForm.setAttribute(this.bypassAttribute, 'true');
      this.setChecking(currentForm, false);

      window.requestAnimationFrame(() => {
        try {
          if (typeof currentForm.requestSubmit === 'function') {
            currentForm.requestSubmit(currentButton || undefined);
          } else {
            currentForm.submit();
          }
        } finally {
          window.setTimeout(() => {
            currentForm.removeAttribute(this.bypassAttribute);
          }, 0);
        }
      });
    },

    async handleSubmit(event) {
      const form = event.target;

      if (
        !(form instanceof HTMLFormElement) ||
        !form.matches('[data-femme-cart-checkout-form]')
      ) {
        return;
      }

      if (form.getAttribute(this.bypassAttribute) === 'true') {
        form.removeAttribute(this.bypassAttribute);
        return;
      }

      /*
        Leave ordinary carts untouched. The rendered drawer data comes directly
        from Shopify Liquid and is sufficient to decide whether this specialised
        validation path is needed.
      */
      if (!this.renderedCartContainsVitall(form)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.clearStatus(form);
      this.setChecking(form, true);

      try {
        /*
          Allow EasyDraw's existing repair controller to remove any orphan or
          duplicate add-on before the final checkout validation.
        */
        await FemmeEasyDrawCartIntegrity.enforce(
          'checkout-integrity',
          { skipRefresh: true }
        );

        const cart = await FemmeCartStateSync.fetchCart();

        if (!FemmeCartStateSync.isCartLike(cart)) {
          throw new Error('Unable to verify the current cart.');
        }

        FemmeCartStateSync.lastCart = cart;
        FemmeCartNavigationSync.rememberCart(cart);

        const issues = this.validate(cart);
        const currentForm =
          form.isConnected ? form : this.getCheckoutForm();

        if (!(currentForm instanceof HTMLFormElement)) {
          throw new Error(
            'The live checkout form disappeared during validation.'
          );
        }

        if (issues.length) {
          this.setError(currentForm, issues);
          this.setChecking(currentForm, false);
          return;
        }

        this.continueCheckout(
          currentForm,
          event.submitter instanceof HTMLElement &&
          event.submitter.isConnected
            ? event.submitter
            : this.getButton(currentForm)
        );
      } catch (error) {
        console.error('[femme Vitall] Checkout integrity check failed.', error);

        const currentForm =
          form.isConnected ? form : this.getCheckoutForm();

        if (currentForm instanceof HTMLFormElement) {
          this.setError(currentForm, [
            'We could not verify your health-test cart. Please try checkout again.',
          ]);
          this.setChecking(currentForm, false);
        }
      }
    },

    init() {
      if (
        document.documentElement.dataset.femmeVitallCheckoutIntegrityBound ===
        'true'
      ) {
        return;
      }

      document.documentElement.dataset.femmeVitallCheckoutIntegrityBound = 'true';

      document.addEventListener(
        'submit',
        (event) => this.handleSubmit(event),
        true
      );

      document.addEventListener('cart:updated', () => {
        document
          .querySelectorAll('[data-femme-cart-checkout-form]')
          .forEach((form) => this.clearStatus(form));
      });
    },
  };

  FemmeVitallCheckoutIntegrity.init();


  FemmeCartNavigationSync.init();
})();
